/**
 * Shared layout maths for the Research Assistant bar-chart artifacts
 * (TELV1-419). Extracted from ArtifactChartBlock so the single-series
 * `bar_chart` renderer and the clustered `grouped_bar_chart` renderer share
 * one implementation of YAxis width + label-wrap estimation instead of
 * drifting copies.
 *
 * Pure functions / constants only — the wrapped YAxis tick COMPONENT lives in
 * its own file (WrappedYAxisTick.tsx) so this module has no component export
 * (keeps react-refresh happy). Bar fill colors are intentionally NOT shared:
 * each renderer keeps its own neutral-palette resolution inline (the TELV1-377
 * invariant test pins that wiring to ArtifactChartBlock.tsx).
 */

/**
 * Characters that roughly fit in one pixel at 11 px font-size in Inter.
 * Recharts Text uses canvas measurement internally, so this is only used for
 * the YAxis `width` computation (not for actual wrapping). Inter characters
 * average ~6.2 px each at 11 px size.
 */
export const CHAR_PX_ESTIMATE = 6.2;
export const Y_AXIS_MIN = 90;
export const Y_AXIS_MAX = 200;

/** Base px per data row when labels are a single line. */
export const BASE_ROW_HEIGHT = 48;
/** Minimum chart height in px. */
export const CHART_MIN_HEIGHT = 200;

/**
 * Compute an appropriate YAxis width from the longest label string.
 * We cap between MIN and MAX so the chart doesn't look odd on either extreme.
 */
export function computeYAxisWidth(items: Array<{ name: string }>): number {
  const longest = Math.max(...items.map((s) => s.name.length));
  const raw = Math.ceil(longest * CHAR_PX_ESTIMATE);
  return Math.min(Math.max(raw, Y_AXIS_MIN), Y_AXIS_MAX);
}

/**
 * Estimate how many lines a label will wrap to given a container width.
 * Used to scale the per-row height so wrapped rows don't overlap.
 */
export function estimateLineCount(label: string, widthPx: number): number {
  const charsPerLine = Math.max(1, Math.floor(widthPx / CHAR_PX_ESTIMATE));
  return Math.ceil(label.length / charsPerLine);
}
